import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { service } from '@ember/service';

import type { PopupData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import CommonAskToConnectButtonComponent from 'webapp/components/common/ask-to-connect-button';
import CommonSecurityInfoComponent from 'webapp/components/common/security-info';
import CommonSecurityInfoSmallComponent from 'webapp/components/common/security-info-small';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import ResolveDataItemChoiceComponent from 'webapp/components/resolve-data-item-choice';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { SourceData } from 'webapp/models-custom/resolve-sources-data';
import type SourceType from 'webapp/models-custom/source-type';

import type { onConnectSourceResult } from 'webapp/services/connect';

export default class SourcePopupResolveComponent extends PopupInnerModalComponent<PopupData.Source.Resolve> {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('connect') declare connectService: Service.Connect;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;

	get model(): SourceType[] {
		return this.popupData.model;
	}

	get sourcesToConnect() {
		const sources: SourceData[] = [];
		this.model.forEach((sourceType) => {
			if (!sourceType.connectedSources.length) {
				sources.push({
					sourceType,
				});
			}
		});
		return sources;
	}

	get sourcesToChoose() {
		const sources: SourceData[] = [];
		this.model.forEach((sourceType) => {
			if (sourceType.connectedSources.length) {
				sources.push({
					sourceType,
				});
			}
		});
		return sources;
	}

	@action
	connect(sourceType: SourceType): void {
		const connectNew = (sourceType: SourceType) => {
			this.analyticsService.event(TrackingEvent.ONBOARDING_CONNECT_CONNECT, {
				datasource: sourceType.key,
			});

			void sourceType
				.connectNew({
					origin: 'modalResolvetransfer',
					connectSourceUsingConnectionDefaults: this.popupData.isOnboarding,
				})
				.then((reply: onConnectSourceResult) => {
					if (reply.isSuccess) {
						const sourceData = reply.source;
						this.popupData.onConnect(sourceData);
					}
				});

			this.close();
		};

		if (sourceType?.isGroup) {
			this.mmSourceTypesService.openSourceTypeInfoPopup(sourceType, (sourceType) => {
				connectNew(sourceType);
			});
		} else {
			connectNew(sourceType);
		}
	}

	@action
	resolveSource(_sourceToResolve: SourceData, sourceId: string | null) {
		const sourceData = { id: sourceId };
		this.popupData.onConnect(sourceData);
		this.close();
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="Connect" @size="lg" />
			<span>Connect data source</span>
		</div>

		<div class="content">
			<p>
				Please connect one of the following integrations (you will be able to connect others
				later):
			</p>

			<div class="actionList">

				<ul class="sources-map">

					{{#each this.sourcesToConnect as |source|}}
						<li class="t1">
							<AvatarOrInitialsComponent
								@name={{source.sourceType.name}}
								@avatar={{source.sourceType.avatar}}
							/>
							<span class="sourceTitle">{{source.sourceType.name}}</span>
							<div class="actions">
								<CommonAskToConnectButtonComponent @model={{source.sourceType}} />
								<SubmitButtonComponent
									@text="Connect"
									@icon="Connect"
									@noDelay={{true}}
									@onTrigger={{fn this.connect source.sourceType}}
									@theme="green"
								/>
							</div>
						</li>
					{{/each}}

					{{#each this.sourcesToChoose as |source|}}
						<li class="t2">
							<AvatarOrInitialsComponent
								@name={{source.sourceType.name}}
								@avatar={{source.sourceType.avatar}}
							/>
							<span class="sourceTitle">{{source.sourceType.name}}</span>
							<ResolveDataItemChoiceComponent
								@sourceData={{source}}
								@onChoose={{this.resolveSource}}
							/>
						</li>
					{{/each}}

				</ul>

			</div>

			{{#if this.popupData.isOnboarding}}
				<CommonSecurityInfoSmallComponent />
			{{else}}
				<CommonSecurityInfoComponent @hideShortExtras={{true}} />
			{{/if}}

		</div>
	</template>
}
